/**
 * WHAT THE OTHER MODULES ARE SHOWING, FOR AWISE (owner, 22 Oct 2026: asked in Support at home why the credit card
 * was not paid off, AWise gave a generic answer — it had no idea which screen or plans were in front of them).
 *
 * The aged care module has had this since 17 Oct (agedCareScreenContext.ts). This is the same tiny store for the
 * rest: each module's plans screen writes its page, a screen key (IN_APP_SCREEN_CONTEXT) and a note of facts
 * already on the screen — the plans shown, their figures, and the debts each keeps and why — and App.tsx reads it
 * when a message is sent. AWise quotes the facts; it never computes from them.
 */
import { formatCurrency } from './index.ts';
import { debtsInPlanWords, type PlanFigures } from '../components/strategy/DebtsInPlanNote.tsx';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

export interface ModuleScreenContext {
    /** The app page this belongs to ("HomeCarePage"); read only while that page is showing. */
    page: string;
    /** The IN_APP_SCREEN_CONTEXT key. */
    screen: string;
    /** Plain facts from the screen. */
    note: string;
}

let current: ModuleScreenContext | null = null;

export function setModuleScreenContext(next: ModuleScreenContext | null): void {
    current = next;
}

export function getModuleScreenContext(page: string): ModuleScreenContext | null {
    return current && current.page === page ? current : null;
}

export interface ShownPlan {
    /** Why it is shown ("Leaves the most"), when the screen says. */
    label?: string | null;
    title: string;
    figures: PlanFigures;
    /** What the plan pays off, in dollars. */
    clearedAmount: number;
    /** The same plan with the debts paid off, where it was tested. */
    twin?: PlanFigures | null;
}

/** The note for a plans screen: doing nothing, each plan shown, and what each does about the debts. */
export function plansScreenNote(i: { factFind: DraftFactFind | null | undefined; years: number; baseline?: PlanFigures | null; plans: ShownPlan[]; extra?: string[]; /** What the plans are compared with, in the module's words. */ baselineLabel?: string }): string {
    const fig = (f: PlanFigures) => `left to the family ${formatCurrency(Math.round(f.estate))} after ${i.years} years, ${f.cashflow < 0 ? `short ${formatCurrency(Math.round(-f.cashflow))}` : `${formatCurrency(Math.round(f.cashflow))} to spare`} a year, Age Pension ${formatCurrency(Math.round(f.pension))} a year`;
    const lines: string[] = [];
    if (i.baseline) lines.push(`${i.baselineLabel || 'The comparison'}: ${fig(i.baseline)}.`);
    i.plans.forEach((p, n) => {
        lines.push(`Plan ${n + 1}${p.label ? ` (${p.label})` : ''}: "${p.title}" — ${fig(p.figures)}.`);
        const debts = debtsInPlanWords({ factFind: i.factFind, clearedAmount: p.clearedAmount, plan: p.figures, twin: p.twin ?? null, years: i.years, context: 'tested' });
        if (debts) lines.push(`Debts in plan ${n + 1}: ${debts.join(' ')}`);
    });
    if (i.extra?.length) lines.push(...i.extra);
    return lines.join('\n');
}
